/**
 * 确定性哈希工具：无副作用、不依赖 `Math.random()` / `Date.now()`，
 * 因此可以安全地用于两端状态一致性校验。
 */

const FNV_OFFSET_BASIS = 0x811c9dc5
const FNV_PRIME = 0x01000193

/** FNV-1a 32 位，返回 8 位小写十六进制 */
export function fnv1a(input: string): string {
  let hash = FNV_OFFSET_BASIS
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, FNV_PRIME) >>> 0
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

/**
 * 确定性序列化：对象键升序、数组保序、null/boolean/number/string 原样、undefined 视为 null。
 *
 * 非有限数字（NaN / Infinity）同样视为 null，因为 JSON 无法表达它们，
 * 若原样输出会让两端算出的指纹不一致。不支持的输入类型（函数、Symbol）一律视为 null。
 */
export function canonicalize(value: unknown): string {
  if (value === null || value === undefined) return 'null'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : 'null'
  if (typeof value === 'string') return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(canonicalize).join(',')}]`
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    const parts = Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalize(record[key])}`)
    return `{${parts.join(',')}}`
  }
  return 'null'
}

/** canonicalize + fnv1a 的组合，用于状态指纹 */
export function hashValue(value: unknown): string {
  return fnv1a(canonicalize(value))
}
